import { type CSSProperties, type ReactNode } from 'react';
import { type RequestContext } from '@cx-web/domain-types';

import { type BookmakersConfig } from '@cx-modules/types';

import { type BettingInsightsContent, type EsIndex } from '../types';

// Type for the context that will be passed as first parameter to render functions
export interface BettingInsightsTableCellContext {
  environment: RequestContext['environment'];
  featureFlags: RequestContext['featureFlags'];
  repubConfig: RequestContext['repubConfig'];
  user: RequestContext['user'];
}

export interface BettingInsightsTableCellConfig<TIndex extends EsIndex> {
  alignItems?: CSSProperties['alignItems'];
  className?: string;
  highlight?: (data: { content: BettingInsightsContent<TIndex> }) => string;
  isFullWidthMobile?: boolean;
  isMain?: boolean;
  justifyContent?: CSSProperties['justifyContent'];
  key: string;
  order?: number;
  render: (
    context: BettingInsightsTableCellContext,
    data: {
      bookmakers: BookmakersConfig;
      content: BettingInsightsContent<TIndex>;
      resultIndex: number;
    },
  ) => ReactNode;
  start?: number;
}

// Type alias for the return type of createBettingInsightsTableCell
export type BettingInsightsTableCell<TIndex extends EsIndex> = Readonly<{
  alignItems?: CSSProperties['alignItems'];
  className: string;
  highlight?: (data: { content: BettingInsightsContent<TIndex> }) => string;
  isFullWidthMobile: boolean;
  isMain: boolean;
  justifyContent?: CSSProperties['justifyContent'];
  key: string;
  order?: number;
  render: (data: {
    bookmakers: BookmakersConfig;
    content: BettingInsightsContent<TIndex>;
    resultIndex: number;
  }) => ReactNode;
  start?: number;
}>;

/**
 * Factory function to create BettingInsightsTableCell configuration (functional approach)
 * @param requestContext - The request context containing environment, feature flags, repub config, and user details
 * @param config - The table cell configuration with render function and optional properties
 * @returns Immutable table cell configuration object with bound render and highlight methods
 * @example
 * ```typescript
 * const cell = createBettingInsightsTableCell(requestContext, {
 *   key: 'odds',
 *   render(context, { bookmakers, content, resultIndex }) {
 *     // context has access to environment, featureFlags, repubConfig, user
 *     return <span>{content.odds}</span>;
 *   },
 *   highlight({ content }) {
 *     return content.isBestOdds ? 'highlight' : '';
 *   },
 *   isMain: true,
 *   justifyContent: 'center',
 *   order: 1
 * });
 * ```
 */
export function createBettingInsightsTableCell<TIndex extends EsIndex>(
  requestContext: RequestContext,
  config: BettingInsightsTableCellConfig<TIndex>,
): BettingInsightsTableCell<TIndex> {
  // Create context object
  const context: BettingInsightsTableCellContext = {
    environment: requestContext.environment,
    featureFlags: requestContext.featureFlags,
    repubConfig: requestContext.repubConfig,
    user: requestContext.user,
  };

  // Create the result object with wrapper function that injects context
  const result = {
    // Config properties
    key: config.key,
    isFullWidthMobile: config.isFullWidthMobile ?? false,
    className: config.className ?? '',
    isMain: config.isMain ?? false,
    alignItems: config.alignItems,
    highlight: config.highlight,
    justifyContent: config.justifyContent,
    order: config.order,
    start: config.start,

    // Wrapper function that injects context as first parameter
    render: (data: { bookmakers: BookmakersConfig; content: BettingInsightsContent<TIndex>; resultIndex: number }) =>
      config.render(context, data),
  };

  return Object.freeze(result);
}
